import type {
  MongoFieldOp,
  MongoFilterExpression,
} from '../../../../util/mongoTypes';
import type {FilterItem, FilterOperator} from '../../../common/FilterBar';
import {REGISTRY_PROJECT_PREFIX} from '../../common/utils';
import type {DiscoverType} from './types';

export type SplitFilters = {
  registryFilter?: string;
  collectionFilter?: string;
  artifactFilter?: string;
};

/**
 * Split filters into registry, collection, and artifact categories
 * based on the discover type context and field-specific routing rules.
 */
export function splitAndSerializeFilters(
  filters: FilterItem[],
  discoverType: 'registries'
): Pick<SplitFilters, 'registryFilter'>;

export function splitAndSerializeFilters(
  filters: FilterItem[],
  discoverType: 'collections'
): Pick<SplitFilters, 'registryFilter' | 'collectionFilter'>;

export function splitAndSerializeFilters(
  filters: FilterItem[],
  discoverType: 'versions'
): SplitFilters;

export function splitAndSerializeFilters(
  filters: FilterItem[],
  discoverType: DiscoverType
): SplitFilters {
  const registryFilters: FilterItem[] = [];
  const collectionFilters: FilterItem[] = [];
  const artifactFilters: FilterItem[] = [];

  for (const filter of filters) {
    let transformedFilter = filter;

    if (filter.field === 'registry_name') {
      // collections/membership queries don't apply registry prefix
      transformedFilter = {
        ...filter,
        field: 'name',
        value: prefixRegistryNameValues(filter.value),
      };
      registryFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'collection_name') {
      transformedFilter = {...filter, field: 'name'};
      collectionFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'source_created_at') {
      transformedFilter = {...filter, field: 'artifact_created_at'};
      artifactFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'linked_at') {
      transformedFilter = {...filter, field: 'acm_created_at'};
      artifactFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'collection_tag') {
      transformedFilter = {...filter, field: 'tag'};
      collectionFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'artifact_tag') {
      transformedFilter = {...filter, field: 'tag'};
      artifactFilters.push(transformedFilter);
      continue;
    }

    if (filter.field === 'alias_or_version') {
      // Alias filter matches both aliases and version indices
      //  (e.g. "v2" matches alias "v2" OR version 2)
      artifactFilters.push({
        ...filter,
        serializeFn: serializeAliasOrVersionFilter,
      });
      continue;
    }

    // Default routing based on the current discover type
    switch (discoverType) {
      case 'registries':
        registryFilters.push(transformedFilter);
        break;
      case 'collections':
        collectionFilters.push(transformedFilter);
        break;
      case 'versions':
        artifactFilters.push(transformedFilter);
        break;
    }
  }

  return {
    registryFilter: serializeFilters(registryFilters),
    collectionFilter: serializeFilters(collectionFilters),
    artifactFilter: serializeFilters(artifactFilters),
  };
}

function prefixRegistryNameValues(
  value: FilterItem['value']
): FilterItem['value'] {
  if (Array.isArray(value)) {
    return value.map(v =>
      typeof v === 'string' && !v.startsWith(REGISTRY_PROJECT_PREFIX)
        ? REGISTRY_PROJECT_PREFIX + v
        : v
    );
  }
  if (typeof value === 'string' && !value.startsWith(REGISTRY_PROJECT_PREFIX)) {
    return REGISTRY_PROJECT_PREFIX + value;
  }
  return value;
}

// Map frontend operators to backend MongoDB-style operators
const operatorMap: Record<FilterOperator, MongoFieldOp> = {
  contains: '$contains', // handled specially
  equals: '$eq',
  notEquals: '$ne',
  notContains: '$contains', // handled specially
  in: '$in',
  notIn: '$nin', // handled specially
  gt: '$gt',
  gte: '$gte',
  lt: '$lt',
  lte: '$lte',
  before: '$lt',
  after: '$gt',
  on: '$eq',
  isEmpty: '$exists', // handled specially
  isNotEmpty: '$exists', // handled specially
};

// Check if a string looks like a version (v2, V10, 123)
function isVersionLike(val: string): boolean {
  return /^v?\d+$/i.test(val);
}

/**
 * Custom serializer for alias_or_version filter: matches alias OR version.
 * Backend converts version strings like "v2" to integers automatically.
 */
export function serializeAliasOrVersionFilter(
  filter: FilterItem
): MongoFilterExpression | null {
  const {value} = filter;

  if (!Array.isArray(value)) {
    return null;
  }

  const aliasValues = value.filter((v): v is string => typeof v === 'string');
  const versionValues = aliasValues.filter(isVersionLike);

  const conditions: MongoFilterExpression[] = [];
  if (aliasValues.length > 0) {
    conditions.push({alias: {$in: aliasValues}});
  }
  if (versionValues.length > 0) {
    conditions.push({version: {$in: versionValues}});
  }

  if (conditions.length === 0) {
    return null;
  }
  return conditions.length === 1 ? conditions[0] : {$or: conditions};
}

// Convert a single FilterItem to backend format
function serializeFilterItem(filter: FilterItem): MongoFilterExpression | null {
  const {field, operator, value} = filter;

  // Valueless operators
  // Works for strings (null or "") or arrays (null or size 0)
  if (operator === 'isEmpty') {
    return {
      $or: [{[field]: {$eq: null}}, {[field]: {$eq: ''}}],
    };
  }
  if (operator === 'isNotEmpty') {
    return {
      $and: [{[field]: {$ne: null}}, {[field]: {$ne: ''}}],
    };
  }

  // Skip other filters with no value
  if (value === undefined || value === null || value === '') {
    return null;
  }

  // Skip empty arrays (multiselect with no selections)
  if (Array.isArray(value) && value.length === 0) {
    return null;
  }

  const backendOp = operatorMap[operator];

  // Handle special cases
  switch (operator) {
    case 'contains':
      // Use $contains (backend LIKE '%value%') instead of $regex so that
      // registry-name prefix is correctly applied
      return {[field]: {$contains: value}};
    case 'notContains':
      return {$not: {[field]: {$contains: value}}};
    case 'in':
      return {[field]: {$in: Array.isArray(value) ? value : [value]}};
    case 'notIn':
      // Also match null so entities with no value aren't excluded
      // SQL's NOT(NULL IN (...)) is NULL (falsy), which drops those rows.
      return {
        $or: [
          {[field]: {$nin: Array.isArray(value) ? value : [value]}},
          {[field]: {$eq: null}},
        ],
      };

    default:
      return {[field]: {[backendOp]: value}};
  }
}

// Convert FilterItem[] to backend JSON string
export function serializeFilters(filters: FilterItem[]): string | undefined {
  const validFilters = filters
    .map(filter => (filter.serializeFn || serializeFilterItem)(filter))
    .filter((f): f is MongoFilterExpression => f !== null);

  if (validFilters.length === 0) {
    return undefined;
  }

  // Wrap multiple filters in $and
  const combined: MongoFilterExpression =
    validFilters.length === 1 ? validFilters[0] : {$and: validFilters};

  return JSON.stringify(combined);
}
